import React, { useState, useEffect, useCallback } from "react";
import Card from "../card/Card";
import Button from "../button/Button";
import { authFetch } from "../../../utils/memorialApi";
import { toast } from "react-toastify";

export function MemorialPrivacySettings({
  memorial,
  onPrivacyUpdate,
  onNotificationUpdate,
  onRefresh,
}) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  const handleModerationToggle = async (checked) => {
    try {
      const res = await authFetch(`${backendUrl}/api/memorials/${memorial._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          moderation: { requireApproval: checked },
        }),
      });
      if (res.ok) {
        toast.success(checked ? "Tribute review enabled" : "Tributes publish immediately");
        if (onRefresh) onRefresh();
      } else {
        toast.error("Could not update moderation settings");
      }
    } catch (e) {
      toast.error("Could not update moderation settings");
    }
  };

  return (
    <Card Width="75vw" className="settings-panel">
      <div className="settings-panel-inner">
        <div className="settings-section-head">
          <p className="dialog-header">Privacy & Notifications</p>
          <p className="settings-sub">
            Control who can see this memorial and when you get updates
          </p>
        </div>

        <div className="dialog-content settings-panel-body">
          <div className="settings-block privacy-checkbox-section">
            <p className="youremail">Privacy</p>
            <div className="privacy-checkbox-group-1">
              <div className="radio-container">
                <label className="custom-radio">
                  <input
                    type="radio"
                    name="privacy"
                    value="onlyme"
                    checked={memorial?.privacy?.onlyme}
                    onChange={() => onPrivacyUpdate(true)}
                  />
                  <span className="radiomark"></span>
                  Visible only to me
                </label>
                <p>
                  Choose this option if you do not want the memorial to be
                  visible to others at this time.
                </p>
              </div>
              <div className="radio-container">
                <label className="custom-radio">
                  <input
                    type="radio"
                    name="privacy"
                    value="public"
                    checked={!memorial?.privacy?.onlyme}
                    onChange={() => onPrivacyUpdate(false)}
                  />
                  <span className="radiomark"></span>
                  All visitors can view and contribute
                </label>
                <p>
                  Recommended for most memorials. This option allows easy
                  access and collaboration.
                </p>
              </div>
            </div>
          </div>

          <div className="settings-block notification-section">
            <p className="youremail">Guest tributes</p>
            <div className="checkbox-container">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={memorial?.moderation?.requireApproval === true}
                  onChange={(e) => handleModerationToggle(e.target.checked)}
                />
                <span className="checkmark"></span>
                Review guest tributes before they appear
              </label>
              <p className="settings-hint">
                When enabled, visitor tributes stay pending until you approve
                them.
              </p>
            </div>
          </div>

          <div className="settings-block notification-section">
            <p className="youremail">Email notifications</p>
            <div className="privacy-checkbox-group-2">
              <div className="checkbox-container">
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={memorial?.notification?.birthday || false}
                    onChange={(e) =>
                      onNotificationUpdate("birthday", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                  On {memorial?.firstName}&apos;s Birthday
                </label>
              </div>
              <div className="checkbox-container">
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={memorial?.notification?.passed || false}
                    onChange={(e) =>
                      onNotificationUpdate("passed", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                  On the day the loved one passed
                </label>
              </div>
              <div className="checkbox-container">
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={memorial?.notification?.post || false}
                    onChange={(e) =>
                      onNotificationUpdate("post", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                  On new posts from other guests
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function MemorialPendingTributes({
  memorial,
  pendingTributes = [],
  selectedTheme,
  paragraphStyle,
  backendUrl,
  memorialId,
  onRefresh,
}) {
  const textColor = selectedTheme?.textColor || "#5c5470";
  const [moderateLoading, setModerateLoading] = useState(null);

  const pendingList =
    pendingTributes.length > 0
      ? pendingTributes
      : (memorial?.about?.tributes || []).filter(
          (t) => (t.status || "approved") === "pending"
        );

  const handleModerateTribute = async (tributeId, action) => {
    setModerateLoading(tributeId);
    try {
      const res = await authFetch(
        `${backendUrl}/api/memorials/${memorialId}/tributes/${tributeId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action }),
        }
      );
      if (res.ok) {
        toast.success(action === "approve" ? "Tribute approved" : "Tribute rejected");
        onRefresh();
      } else {
        toast.error("Could not update tribute");
      }
    } catch (e) {
      toast.error("Could not update tribute");
    } finally {
      setModerateLoading(null);
    }
  };

  return (
    <Card Width="90%" className="settings-panel">
      <div className="settings-panel-inner">
        <div className="settings-section-head">
          <p className="manage-title2" style={{ color: textColor }}>
            Pending tributes
            {pendingList.length > 0 ? ` (${pendingList.length})` : ""}
          </p>
          <p className="settings-sub">
            Review guest messages before they appear on the memorial
          </p>
        </div>

        {pendingList.length === 0 ? (
          <div className="settings-empty" role="status">
            <p>No tributes awaiting review.</p>
            <span>New guest tributes will show up here when review is on.</span>
          </div>
        ) : (
          <div className="settings-pending-list">
            {pendingList.map((tribute) => (
              <div key={tribute._id} className="manage-box settings-pending-item">
                <p style={{ ...paragraphStyle, fontWeight: 600 }}>
                  {tribute.user?.name || tribute.user?.email || "Guest"}
                </p>
                <p style={paragraphStyle}>{tribute.description}</p>
                <div className="manage-btn-section settings-action-row">
                  <button
                    type="button"
                    className="pending-btn"
                    disabled={moderateLoading === tribute._id}
                    onClick={() => handleModerateTribute(tribute._id, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="visited-btn"
                    disabled={moderateLoading === tribute._id}
                    onClick={() => handleModerateTribute(tribute._id, "reject")}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function MemorialTransferSettings({ memorial, selectedTheme, backendUrl, memorialId }) {
  const textColor = selectedTheme?.textColor || "#5c5470";
  const [transferEmail, setTransferEmail] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchPendingTransfer = useCallback(async () => {
    if (!memorialId) return;
    setPendingLoading(true);
    try {
      const res = await authFetch(
        `${backendUrl}/api/memorials/${memorialId}/transfer/pending`
      );
      if (res.ok) {
        const data = await res.json();
        setPendingTransfer(data.transfer || null);
      }
    } catch (e) {
      console.error("fetchPendingTransfer:", e);
    } finally {
      setPendingLoading(false);
    }
  }, [backendUrl, memorialId]);

  useEffect(() => {
    fetchPendingTransfer();
  }, [fetchPendingTransfer]);

  const formatExpiryLabel = (transfer) => {
    if (!transfer?.expiresAt) return "";
    const msLeft = new Date(transfer.expiresAt).getTime() - Date.now();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
    if (daysLeft === 0) return "Expires today";
    if (daysLeft === 1) return "Expires in 1 day";
    return `Expires in ${daysLeft} days`;
  };

  const handleCancelPending = async () => {
    if (!pendingTransfer?._id) return;
    setCancelLoading(true);
    try {
      const res = await authFetch(`${backendUrl}/api/transfers/${pendingTransfer._id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success("Transfer invitation cancelled");
        setPendingTransfer(null);
      } else {
        const data = await res.json();
        toast.error(data.error || "Could not cancel transfer");
      }
    } catch (e) {
      toast.error("Could not cancel transfer");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferEmail.trim()) {
      toast.error("Enter recipient email");
      return;
    }
    setTransferLoading(true);
    try {
      const res = await authFetch(`${backendUrl}/api/memorials/${memorialId}/transfer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toEmail: transferEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.awaitingSignup) {
          toast.success(
            "Invitation sent. They'll receive an email to create an account and accept the transfer."
          );
        } else {
          toast.success("Transfer invitation sent");
        }
        setTransferEmail("");
        setPendingTransfer(data.transfer || null);
        await fetchPendingTransfer();
      } else {
        toast.error(data.error || "Transfer failed");
      }
    } catch (e) {
      toast.error("Transfer failed");
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <Card Width="75vw" className="settings-panel">
      <div className="settings-panel-inner">
        <div className="settings-section-head">
          <p className="dialog-header">Transfer ownership</p>
          <p className="settings-sub">
            Pass admin access to someone else by email
          </p>
        </div>

        <div className="dialog-content settings-panel-body">
          <p className="settings-lead">
            Transfer admin access for {memorial?.firstName}&apos;s memorial to
            another person. If they don&apos;t have an account yet, we&apos;ll
            invite them to join WillAlwaysLoveU. The memorial plan and payment
            history stay with the original purchaser.
          </p>
          <p className="settings-warning">
            You will lose admin access once they accept.
          </p>

          {pendingLoading ? (
            <p className="settings-hint">Checking pending invitations…</p>
          ) : pendingTransfer ? (
            <div className="settings-pending-transfer">
              <p className="settings-pending-transfer-title">
                Pending transfer invitation
              </p>
              <p className="settings-lead">
                Waiting for <strong>{pendingTransfer.toEmail}</strong>
                {!pendingTransfer.toUserId && " to create an account"}
              </p>
              <p className="settings-hint">
                {formatExpiryLabel(pendingTransfer)}
                {!pendingTransfer.toUserId &&
                  " · Cancelled automatically if they don't sign up in 3 days"}
              </p>
              <div className="settings-action-row">
                <Button
                  text="Cancel invitation"
                  isLoading={cancelLoading}
                  onClick={handleCancelPending}
                  bgColor="#a04444"
                />
              </div>
            </div>
          ) : (
            <div className="settings-transfer-form">
              <input
                type="email"
                className="title-input settings-email-input"
                placeholder="Recipient email address"
                value={transferEmail}
                onChange={(e) => setTransferEmail(e.target.value)}
              />
              <div className="settings-action-row">
                <Button
                  text="Send invitation"
                  isLoading={transferLoading}
                  onClick={handleTransfer}
                  bgColor={textColor}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
